import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Pages a scroll-snapping row of slides with previous/next arrows and keeps the
 * "[label] 1 of N" counter and arrow disabled states in sync with the scroll position.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} track - The scrolling row; each child element is a slide.
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 * @property {HTMLElement} [counter]
 *
 * @extends {Component<Refs>}
 */
class CarouselComponent extends Component {
  requiredRefs = ['track'];

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number | undefined} */
  #frame;

  connectedCallback() {
    super.connectedCallback();
    this.#listen();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#listen();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    if (this.#frame) cancelAnimationFrame(this.#frame);
  }

  previous() {
    this.#page(-1);
  }

  next() {
    this.#page(1);
  }

  #listen() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.refs.track.addEventListener('scroll', this.#scheduleUpdate, { passive: true, signal });
    window.addEventListener('resize', this.#scheduleUpdate, { passive: true, signal });
    this.#update();
  }

  #scheduleUpdate = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = undefined;
      this.#update();
    });
  };

  /** @returns {HTMLElement[]} */
  get #slides() {
    return /** @type {HTMLElement[]} */ (Array.from(this.refs.track.children));
  }

  /** Distance between the starts of two neighbouring slides. */
  get #step() {
    const [first, second] = this.#slides;
    if (!first) return 0;
    if (!second) return first.offsetWidth;
    return Math.abs(second.offsetLeft - first.offsetLeft);
  }

  /** @param {number} direction - 1 for forward, -1 for back, in reading order. */
  #page(direction) {
    const rtl = getComputedStyle(this).direction === 'rtl';
    this.refs.track.scrollBy({
      left: this.#step * direction * (rtl ? -1 : 1),
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }

  #update() {
    const { track, previous, next, counter } = this.refs;
    const step = this.#step;
    const scrolled = Math.abs(track.scrollLeft);
    const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);

    // Count arrow steps (scroll positions), not slides: with 3 visible and 4 slides there are 2 positions.
    const slideCount = this.#slides.length;
    const total = slideCount && step ? Math.min(slideCount, Math.round(maxScroll / step) + 1) : Math.min(slideCount, 1);
    const current = total && step ? Math.min(total, Math.round(scrolled / step) + 1) : Math.min(total, 1);

    if (previous) previous.disabled = scrolled <= 1;
    if (next) next.disabled = scrolled >= maxScroll - 1;

    if (counter) {
      const template = this.dataset.counterTemplate ?? '';
      counter.textContent = total
        ? template.replace('[current]', String(current)).replace('[total]', String(total))
        : '';
    }
  }
}

if (!customElements.get('carousel-component')) {
  customElements.define('carousel-component', CarouselComponent);
}
